/*
 * @description: DOM 操作类集合
 * @author: Jason Wong
 * @since: 2019-12-11 17:25:04
 */

import { isBlank } from "./String";

/**
 * 判定一个HTML节点是否为另外一个节点的祖先节点
 * @param {Node} ancestor
 * @param {Node} child
 */
function isAncestorOf(ancestor, child) {
    if (!ancestor || !child) return;

    let curEl = child;
    while (curEl) {
        if (curEl == ancestor) return true;
        curEl = curEl.parentNode;
    }

    return false;
}

/**
 *
 * @param {(el:Element)=>Boolean} ancestorFilter
 * @param {Element} child
 */
function underAncestorLike(ancestorFilter, child) {
    if (!ancestorFilter || !child) return false;

    let curEl = child;
    while (!!curEl) {
        if (ancestorFilter.call(null, curEl)) return true;
        curEl = curEl.parentElement;
    }
    return false;
}

/**
 * addEventListener的简写
 * @param {*} el
 * @param {*} event
 * @param {*} handle
 */
function on(el, event, handle) {
    el.addEventListener(event, handle);
}
/**
 * removeEventListener的简写
 * @param {*} el
 * @param {*} event
 * @param {*} handler
 */
function off(el, event, handler) {
    el.removeEventListener(event, handle);
}

/**
 * 判定一个节点是否是textNode
 * @param {*} node
 */
function isTextNode(node) {
    return node && node.nodeType == 3;
}

/**
 * 将节点内的#text节点合并在一起
 * @param {Node} node
 */
function combineText(node) {
    let text = node.textContent;
    let startElement = node;
    let endElement = node;

    let curNode = node.previousSibling;
    while (curNode && isTextNode(curNode)) {
        startElement = curNode;
        text = curNode.textContent + text;
        curNode = curNode.previousSibling;
    }
    curNode = node.nextSibling;
    while (curNode && curNode.previousSibling && isTextNode(curNode)) {
        endElement = curNode;
        text += curNode.textContent;
        curNode = curNode.nextSibling;
    }

    let range = document.createRange();
    range.setStartBefore(startElement);
    range.setEndAfter(endElement);
    range.deleteContents();
    const newText = document.createTextNode(text);
    range.insertNode(newText);
    range.detach();

    return newText;
}

/**
 * 获取当前的Section对象并将光标移动到它的后面
 * @param {*} elementNode
 */
function setCursorAfter(elementNode) {
    let section = document.getSelection();
    if (section && section.rangeCount > 0) {
        let range = section.getRangeAt(0);
        range.setEndAfter(newText);
        range.collapse(elementNode);
    }
}

/**
 * 获取当前选中的Range
 */
function getSectionRange() {
    const section = document.getSelection();
    return section && section.rangeCount > 0 ? section.getRangeAt(0) : null;
}

/**
 * 获取HTML节点值或者给HTML节点属性赋值
 * @param {*} el
 * @param {*} name
 * @param {*} value
 */
function attr(el, name, value) {
    if (!el) return;

    if (value) el.setAttribute(name, value);
    else {
        for (var i = 0; i < el.attributes.length; i++) {
            if (el.attributes[i].name == name) {
                return el.attributes[i].value;
            }
        }
        return null;
    }
}

function getDialogBodyElement(dialog) {
    if (!dialog) return null;
    if (!dialog.$el || !dialog.$el.childNodes || !dialog.$el.childNodes.length) {
        return null;
    }
    return dialog.$el.childNodes[0];
}

const NODE_TYPE = {
    element: 1,
    attr: 2,
    text: 3,
    cdata: 4,
    entityRef: 5,
    entity: 6,
    process: 7,
    comment: 8,
    document: 9,
    docType: 10,
    docFrag: 11,
    nonation: 12,
};

/**
 * 部分类型的文件在被拖拽进浏览器的时候会直接显示，导致拖拽逻辑失效，
 * 所以需要阻止原始的浏览器逻辑
 */
function preventDocumentDefaultDrag() {
    document.ondragover = function (e) {
        e.preventDefault();
    };
    document.ondrop = function (e) {
        e.preventDefault();
    };
}

function preventDefault() {
    return false;
}

/**
 *
 * @param {String|Element} elOrId
 */
function getElement(elOrId) {
    let element = elOrId;
    if (typeof elOrId === "string") {
        element = document.getElementById(elOrId);
    }
    return element;
}

/**
 *
 * @param {String|Element} el
 */
function scrollIntoViewer(el) {
    const element = getElement(el);
    if (element) {
        element.scrollIntoView({
            behavior: "smooth",
        });
    }
}

/**
 *
 * @param {String|Element} el
 */
function focus(el) {
    const element = getElement(el);
    element && element.focus();
}

/**
 *
 * @param {Element} el
 * @param {Element} elContainer
 */
function isInView(el, elContainer) {
    if (!el) return false;

    const elRect = el.getBoundingClientRect();
    const elContainerRect = elContainer.getBoundingClientRect();
    return elRect.y >= elContainerRect.y && elRect.y < elContainerRect.bottom;
}

function ignoreEnterInForm() {
    document.onkeydown = function (event) {
        var target, code, tag;
        if (!event) {
            event = window.event; //针对ie浏览器
            target = event.srcElement;
            code = event.keyCode;
            if (code == 13) {
                tag = target.tagName;
                if (tag == "TEXTAREA") {
                    return true;
                } else {
                    return false;
                }
            }
        } else {
            target = event.target; //针对遵循w3c标准的浏览器，如Firefox
            code = event.keyCode;
            if (code == 13) {
                tag = target.tagName;
                if (tag == "INPUT") {
                    return false;
                } else {
                    return true;
                }
            }
        }
    };
}

/**
 *
 * @param {HTMLDivElement} el
 */
function getScrollContainer(el) {
    if (!el) return null;
    let curEl = (isTextNode(el) ? el.parentElement : el).offsetParent;
    while (curEl) {
        if (curEl.scrollHeight > curEl.clientHeight) {
            return curEl;
        }
        curEl = curEl.offsetParent;
    }
    return null;
}

/**
 *
 * @param {Element} oldNode
 * @param  {...Element} newNodes
 * @returns
 */
function insertNodeAfter(oldNode, ...newNodes) {
    if (!newNodes || newNodes.length < 1) return;

    const parentElement = oldNode.parentElement;
    if (!oldNode.nextSibling) {
        for (const newNode of newNodes) {
            parentElement.appendChild(newNode);
        }
    } else {
        const oldNextNode = oldNode.nextSibling;
        for (const newNode of newNodes) {
            parentElement.insertBefore(newNode, oldNextNode);
        }
    }
}

/**
 *
 * @param {String} htmlString
 */
function getTextOfHTMLString(htmlString, exludeFilter) {
    if (isBlank(htmlString)) return htmlString;
    const el = $(`<div>${htmlString}</div>`)[0];
    if (!!exludeFilter && exludeFilter instanceof Function) {
        let curChild;
        for (let i = el.childNodes.length - 1; i > -1; i--) {
            curChild = el.childNodes[i];
            if (exludeFilter(curChild)) curChild.remove();
        }
    }
    return el.innerText;
}

/**
 *
 * @param {Element} el
 */
function findFirstTransformParent(el) {
    console.log("#-# findFirstTransformParent");
    if (!el) return null;
    let curEl = el.parentElement;
    while (curEl) {
        if (!!curEl.style.transform && curEl.style.transform.toLowerCase().indexOf("scale") > -1) {
            return curEl;
        }
        curEl = curEl.parentElement;
    }
    return null;
}

/**
 *
 * @param {HTMLSpanElement} el
 */
function limitLines(el, maxHeight) {
    if (!el) return;
    if (el.clientHeight <= maxHeight) {
        return;
    }

    const originalOpacity = el.style.opacity;
    el.style.opacity = "0.01";
    try {
        let leftText = el.textContent;
        while (el.clientHeight > maxHeight) {
            el.textContent = (leftText = leftText.substring(0, leftText.length - 1)) + "...";
        }
    } finally {
        el.style.opacity = originalOpacity;
    }
}

/**
 *
 * @param {Element} el
 */
function waveRippleAtClickHandler(ev) {
    if (!ev) return;

    let target = ev.target;
    while (target) {
        if (!!target.classList && target.classList.contains("ripplet")) {
            break;
        }
        target = target.parentElement;
    }
    if (!target) return;

    const rippleEl = document.createElement("div");
    rippleEl.classList.add("wave-ripple");
    const rect = target.getBoundingClientRect();
    const left = ev.clientX - rect.left - target.clientWidth / 2;
    const top = ev.clientY - rect.top - target.clientHeight / 2;
    rippleEl.style.left = `${left}px`;
    rippleEl.style.top = `${top}px`;
    rippleEl.addEventListener("transitionstart", (ev) => {
        rippleEl.addEventListener("transitionend", (ev1) => {
            rippleEl.remove();
        });
    });
    rippleEl.style.width = `${target.clientWidth}px`;
    rippleEl.style.height = `${target.clientHeight}px`;
    target.appendChild(rippleEl);

    setTimeout(() => {
        rippleEl.classList.add("animation");
    }, 0);
}

export {
    on,
    off,
    isTextNode,
    combineText,
    setCursorAfter,
    getSectionRange,
    attr,
    NODE_TYPE,
    getDialogBodyElement,
    preventDocumentDefaultDrag,
    scrollIntoViewer,
    focus,
    isAncestorOf,
    underAncestorLike,
    isInView,
    ignoreEnterInForm,
    getScrollContainer,
    preventDefault,
    insertNodeAfter,
    getTextOfHTMLString,
    findFirstTransformParent,
    limitLines,
    waveRippleAtClickHandler,
};
